import { ChevronLeftIcon, ChevronRightIcon } from "lucide-react";
import type { HTMLAttributes, ReactNode } from "react";
import { forwardRef } from "react";

import { SidebarTrigger, useSidebar } from "@/components/ui/sidebar";
import { cn } from "@/components/ui/utils";
import { hasElectronTitlebar, isElectron } from "@/lib/electron";

const SubHeader = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ children, className, ...props }, ref) => {
    const hasTitlebar = hasElectronTitlebar();
    const { state } = useSidebar();
    return (
      <div
        ref={ref}
        {...props}
        className={cn(
          "flex items-center h-12 w-full bg-background shrink-0 px-3 border-b overflow-hidden rounded-none md:rounded-tl-lg",
          hasTitlebar && "electron-drag",
          hasTitlebar && state === "collapsed" && "pl-20",
          className,
        )}
      >
        {children}
      </div>
    );
  },
);

function SubHeaderLeft({
  children,
  className,
  action,
}: {
  children: ReactNode;
  className?: string;
  action?: ReactNode;
}) {
  return (
    <div className="flex gap-2 flex-1 min-w-0">
      {isElectron() && <NavButtons />}
      {action ? action : <SidebarTrigger />}
      <div className={cn("flex items-center text-sm min-w-0", className)}>
        {children}
      </div>
    </div>
  );
}

function SubHeaderRight({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn("flex items-center justify-end", className)}>
      {children}
    </div>
  );
}

function NavButtons() {
  return (
    <div className="flex items-center gap-0.5">
      <button
        type="button"
        onClick={() => window.history.back()}
        className="inline-flex items-center justify-center size-7 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
        aria-label="Go back"
      >
        <ChevronLeftIcon className="size-4" />
      </button>
      <button
        type="button"
        onClick={() => window.history.forward()}
        className="inline-flex items-center justify-center size-7 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
        aria-label="Go forward"
      >
        <ChevronRightIcon className="size-4" />
      </button>
    </div>
  );
}

const Namespace = Object.assign(SubHeader, {
  Left: SubHeaderLeft,
  Right: SubHeaderRight,
});

export { Namespace as SubHeader };
